/**
 * Owner: Haselectedton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow

import type { Element } from 'react';

import React, { useState } from 'react';
import { Alert, Button, Card, CardBody, CardText, CardTitle, Col, Row, Spinner } from 'reactstrap';
import Header from '#components/support/layout/Header/index.js';
import Main from '#components/support/layout/MainPageContainer/index.jsx';
import Page from '#components/support/layout/Page/index.js';
import { modelTypesById } from '#src/js/constants/models/modelTypes/index.js';

type Props = {|
  handleCancelClick: Function,
  handleContinueClick: Function,
  modelTypes?: Array<number>,
  includeHeader?: boolean,
|};

/*
Renders a description string, showing any "Vs30" with a subscripted 30 to match the notation
used elsewhere in the app (e.g. T<sub>1</sub>, S<sub>1</sub>). Descriptions without "Vs30" pass
through unchanged.
 */
const renderDescription = (text: string) => {
  const token = 'Vs30';
  const idx = text.indexOf(token);
  if (idx === -1) {
    return text;
  }
  return (
    <>
      {text.slice(0, idx)}
      Vs<sub>30</sub>
      {text.slice(idx + token.length)}
    </>
  );
};

/*
modelTypes is the org's permitted model type ids, so the page offers a Create button only for
types the org may actually create — POST /model rejects anything else with a 403
(inOrgWithModelTypePermission). Looping the ids and looking each one up keeps this driven by
permissions rather than by the full modelTypes constant. Ids with no matching definition are
dropped so a stale permission value cannot blank the page.
 */
const ModelTypesPage = ({
  handleCancelClick,
  handleContinueClick,
  modelTypes = [],
  includeHeader = true,
}: Props): Element<typeof Page> => {
  const [creatingId, setCreatingId] = useState(null);
  const [error, setError] = useState(null);

  // Choosing a type creates the model immediately (UI design §2.1; issue #127 removed the interim
  // naming step). The clicked card spins and every Create button disables meanwhile, so a
  // double-click cannot create two models. On success the flow navigates away and this unmounts;
  // on failure we surface the error and re-enable, rather than silently dropping the spinner.
  const onSelectType = id => {
    const result = handleContinueClick(id);
    if (result && typeof result.then === 'function') {
      setError(null);
      setCreatingId(id);
      result
        .catch(() => setError('Something went wrong creating the model. Please try again.'))
        .finally(() => setCreatingId(null));
    }
  };

  const modelTypeOptions = modelTypes
    .map(typeId => modelTypesById[typeId])
    .filter(Boolean)
    .map(type => {
      const { description, id, name } = type;
      const isThisTypeCreating = creatingId === id;
      return (
        <Col key={id} md="4" className="col">
          <Card id={`selectType${id}ModelCard`} className="h-100">
            <CardBody className="d-flex flex-column">
              <CardTitle>
                <strong>{name}</strong>
              </CardTitle>
              <CardText>{renderDescription(description)}</CardText>
              <div className="mt-auto text-right">
                <Button
                  id={`createButtonType${id}`}
                  className="btn-continue mt-3"
                  color="primary"
                  disabled={creatingId !== null}
                  onClick={() => onSelectType(id)}
                >
                  {isThisTypeCreating ? (
                    <Spinner size="sm" aria-label="Creating model..." />
                  ) : (
                    'Create'
                  )}
                </Button>
              </div>
            </CardBody>
          </Card>
        </Col>
      );
    });
  return (
    <Page className="model-type-page">
      {includeHeader && <Header />}
      <Main>
        <Row>
          <Col md="12" className="col">
            <h1>Choose Model Type</h1>
            <p>What type of model would you like to create?</p>
          </Col>
        </Row>
        {error && (
          <Row>
            <Col md="12" className="col">
              <Alert color="danger">{error}</Alert>
            </Col>
          </Row>
        )}
        <Row>{modelTypeOptions}</Row>
        <Row>
          <Col md="12" className="col">
            <Button
              className="btn-cancel mt-3"
              color="link"
              disabled={creatingId !== null}
              onClick={handleCancelClick}
            >
              Cancel
            </Button>
          </Col>
        </Row>
      </Main>
    </Page>
  );
};

export default ModelTypesPage;
